import React from 'react';
import SectionTag from '../components/SectionTag';
import ScrollReveal from '../components/ScrollReveal';
import ScreenshotCard from '../components/ScreenshotCard';

const FEATURES = [
  {
    tag: 'COMMAND CENTER', number: '01',
    heading: 'Your Organization at a Glance',
    desc: 'Get a real-time overview of everything happening in your organization — active resources, upcoming events, recent inquiries, and engagement metrics — all from one clean, powerful dashboard.',
    bullets: ['View active listings at a glance', 'Monitor inquiries in real time', 'Track overall platform engagement'],
    image: 'dashboard.png',
    badge: 'Live Dashboard',
    stats: { label: 'Events Created', value: '06', sub: 'Active this month' },
  },
  {
    tag: 'PUBLISHING TOOLS', number: '02',
    heading: 'Create Events in Minutes',
    desc: 'Our intuitive event creation flow lets you publish detailed, visually rich event listings with everything people need to show up — name, description, banner image, date, time, and location.',
    bullets: ['Upload rich banner images', 'Set precise date and time', 'Rich text description editor'],
    image: 'event-creation.png',
    badge: 'Post Event',
    stats: { label: 'Category', value: 'Food Drive', sub: 'Published & live' },
  },
  {
    tag: 'ORGANIZATION', number: '03',
    heading: 'Manage Everything in One Place',
    desc: 'A clean, organized list view of all your published resources and events. Quickly edit, pause, or remove listings — and keep your community information always up to date.',
    bullets: ['Edit or remove listings instantly', 'Filter by status or date', 'Organized list with quick actions'],
    image: 'event-list.png',
    badge: 'Manage Events',
    stats: { label: 'Total Events', value: '06', sub: 'Across all statuses' },
  },
  {
    tag: 'COMMUNITY ENGAGEMENT', number: '04',
    heading: 'Connect Directly With People in Need',
    desc: 'When someone reaches out through the People In Need app, you receive their inquiry directly on your dashboard. Respond promptly and build real relationships with the people you serve.',
    bullets: ['Receive inquiries instantly', 'Reply directly from the dashboard', 'Full inquiry history and context'],
    image: 'inquiries.png',
    badge: 'Live Inquiries',
    stats: { label: 'New Inquiry', value: '1', sub: 'Awaiting your reply' },
  },
  {
    tag: 'PERSONAL SPACE', number: '05',
    heading: 'Your Private Document Hub',
    desc: 'Store your important documents securely in your Personal Space. Choose to keep them private or make them visible to other non-profits — giving your organization full control over what you share and with whom.',
    bullets: ['Upload PDFs, images, and more', 'Set visibility to public or private', 'Share selectively with other non-profits'],
    image: 'personal-space.png',
    badge: 'Personal Space',
    stats: { label: 'Storage Used', value: '1.45MB', sub: 'of 500MB available' },
  },
];

function FeatureRow({ feature, index }) {
  const isOdd = index % 2 === 0;

  const textCol = (
    <ScrollReveal direction={isOdd ? 'left' : 'right'} delay={0}>
      <div style={{ maxWidth: '480px' }}>
        <div
          className="inline-block mb-3"
          style={{
            background: 'rgba(63,196,227,0.1)', color: '#3FC4E3',
            fontFamily: 'popinsMedium', fontSize: '12px',
            padding: '4px 12px', borderRadius: '6px', letterSpacing: '0.06em',
          }}
        >
          {feature.tag}
        </div>
        <div className="relative mb-3">
          <span
            className="absolute select-none pointer-events-none"
            style={{ fontFamily: 'popinsBold', fontSize: '48px', color: 'rgba(63,196,227,0.15)', top: '-20px', left: '-8px', lineHeight: 1 }}
          >
            {feature.number}
          </span>
          <h3 style={{ fontFamily: 'popinsBold', fontSize: 'clamp(20px, 2.5vw, 28px)', color: '#1B1A28', lineHeight: 1.2, position: 'relative', zIndex: 1 }}>
            {feature.heading}
          </h3>
        </div>
        <p style={{ fontFamily: 'popinsRegular', fontSize: '16px', color: '#6B7280', lineHeight: '1.8', marginBottom: '20px' }}>
          {feature.desc}
        </p>
        <ul className="flex flex-col gap-2">
          {feature.bullets.map((b) => (
            <li key={b} className="flex items-center gap-2">
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3FC4E3', flexShrink: 0, display: 'inline-block' }} />
              <span style={{ fontFamily: 'popinsRegular', fontSize: '14px', color: '#6B7280' }}>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </ScrollReveal>
  );

  const screenshotCol = (
    <ScrollReveal direction={isOdd ? 'right' : 'left'} delay={200}>
      <ScreenshotCard
        src={`/nonprofit/${feature.image}`}
        alt={feature.heading}
        stats={feature.stats}
        badge={feature.badge}
      />
    </ScrollReveal>
  );

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center feature-row" style={{ padding: '80px 0' }}>
        {isOdd
          ? <><div>{textCol}</div><div>{screenshotCol}</div></>
          : <><div className="order-last lg:order-first">{screenshotCol}</div><div className="order-first lg:order-last">{textCol}</div></>
        }
      </div>
      <div style={{ height: '1px', background: '#E5E5E5' }} />
    </div>
  );
}

export default function NonProfits() {
  return (
    <section id="nonprofits" style={{ background: '#ffffff', padding: '120px 5% 80px' }}>
      <div className="max-w-screen-xl mx-auto">
        <div className="flex flex-col items-center text-center mb-4">
          <SectionTag label="FOR NON-PROFITS" />
          <h2 style={{ fontFamily: 'popinsBold', fontSize: 'clamp(26px, 4vw, 44px)', color: '#1B1A28', marginBottom: '12px' }}>
            Everything Your Organization Needs
          </h2>
          <p style={{ fontFamily: 'popinsRegular', fontSize: '16px', color: '#6B7280', maxWidth: '600px', textAlign: 'center' }}>
            A powerful dashboard built to help non-profits reach more people, manage events, and measure their community impact.
          </p>
        </div>

        {FEATURES.map((feature, i) => (
          <FeatureRow key={feature.number} feature={feature} index={i} />
        ))}

        {/* CTA */}
        <ScrollReveal direction="up">
          <div className="flex flex-col items-center text-center mt-16 gap-4">
            <h3 style={{ fontFamily: 'popinsSemiBold', fontSize: 'clamp(22px, 3vw, 32px)', color: '#1B1A28' }}>
              Ready to Make a Difference?
            </h3>
            <p style={{ fontFamily: 'popinsRegular', fontSize: '16px', color: '#6B7280' }}>
              Join TCCplus and start connecting your organization with your community today.
            </p>
            <a
              href="https://connect.countyconnectplus.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-all duration-300 mt-2"
              style={{
                display: 'inline-flex', alignItems: 'center',
                height: '52px', padding: '0 32px',
                background: 'linear-gradient(135deg, #3FC4E3, #006077)',
                color: '#ffffff', borderRadius: '12px',
                fontFamily: 'popinsSemiBold', fontSize: '16px', textDecoration: 'none',
                boxShadow: '0 4px 16px rgba(63,196,227,0.3)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(63,196,227,0.45)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(63,196,227,0.3)'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              Go to Nonprofit Portal →
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
